/**
 * Chat Turn — orchestrates one user request end to end: gather spreadsheet
 * context, build the prompt, send it to the agent (streamed), parse any
 * action blocks out of the response, and run them against Excel.
 *
 * Dependencies are injectable (with real implementations as defaults) so
 * this can be exercised without Office.js or a network call.
 */

import {sendMessage, ChatMessage} from "./librechatApi";
import {
  capHistory,
  capHistoryWithDiagnostics,
  pruneSnapshots,
  toChatMessages,
  turnIndexOf,
  TurnMessage,
} from "./conversationHistory";
import {
  getContextData,
  getContextDataWithDiagnostics,
  getExcelSettings,
  getExcelSettingsWithDiagnostics,
  ExcelSettings,
} from "./excelContext";
import {parseActions, SpreadsheetAction} from "./actionParser";
import {executeActions, ActionResult, ConfirmFn} from "./actionExecutor";
import {buildUserTurn, buildFollowUpTurn} from "./systemPrompt";
import {logError} from "./debugLogger";
import {
  ClassifiedError,
  ErrorKind,
  classifyError,
  errorMessageOf,
  isAbortError,
} from "./errorKind";
import {
  recordTelemetryEvent,
  generateTurnId,
  getSessionId,
  getAddonEnv,
  TelemetryEvent,
} from "./telemetry";
import {PhaseClock, TurnPhase, createPhaseClock} from "./turnPhases";

// Re-exported so callers keep importing the phase vocabulary from the module
// that reports it; it lives in `turnPhases.ts` only so `otel.js`'s copy can be
// pinned against it without importing the turn loop. See that module.
export type {TurnPhase} from "./turnPhases";

/** Default loop iteration cap, used when no override is configured in Settings. */
export const MAX_ITERATIONS = 5;
/** Safety rail on the configurable cap: values outside this range are clamped. */
export const MIN_ITERATIONS = 1;
export const MAX_ITERATIONS_LIMIT = 10;
const MAX_VALIDATION_REPAIRS = 2;

function isRepairableFailure(result: ActionResult): boolean {
  return (
    result.outcome === "failure" &&
    (result.retryable === true || result.errorCode === "script_validation")
  );
}

function clampIterationCap(value: number | undefined): number {
  if (value === undefined || Number.isNaN(value)) return MAX_ITERATIONS;
  return Math.min(
    Math.max(Math.trunc(value), MIN_ITERATIONS),
    MAX_ITERATIONS_LIMIT,
  );
}

/**
 * Where the turn has got to. A long turn is mostly a turn taking several
 * passes, so the step number is what tells a waiting user the difference
 * between slow progress and a stall.
 */
export interface TurnProgress {
  /** 1-based pass this phase belongs to; 0 before the first request goes out. */
  iteration: number;
  /** The iteration cap in force, after clamping. */
  of: number;
}

export interface ChatTurnParams {
  agentId: string;
  apiKey: string;
  userMessage: string;
  /**
   * Overrides the default loop iteration cap. Clamped to the 1–10 safety
   * rail regardless of what's passed in.
   */
  maxIterations?: number;
  /** Conversation so far, as returned by a previous turn. */
  history?: TurnMessage[];
  /**
   * Ends the turn early. The in-flight request is dropped and no further
   * actions run, but actions already applied to the workbook stand.
   */
  signal?: AbortSignal;
  /**
   * Confirms a destructive script before it runs. `window.confirm` isn't
   * supported in the Excel task pane, so the caller must supply a UI-driven
   * one; omitting it falls back to `executeActions`' own default.
   */
  confirm?: ConfirmFn;
}

export interface ChatTurnCallbacks {
  /** Fired before each iteration's request, so the caller can open a new message. */
  onIterationStart?: () => void;
  /**
   * Fired whenever the turn moves to a new stage of work. Phases are a closed
   * union rather than display strings so the wording (and its language) stays
   * in the UI layer.
   */
  onPhase?: (phase: TurnPhase, progress: TurnProgress) => void;
  /** Fired once the request messages have been assembled, before they're sent. */
  onPromptBuilt?: (messages: ChatMessage[]) => void;
  /** Fired for each streamed chunk of the assistant's response. */
  onChunk?: (chunk: string) => void;
  /**
   * Fired once an iteration's response is parsed, with the text worth
   * keeping and the raw JSON of any action blocks emitted (for an optional
   * collapsible "show instructions" view).
   */
  onIterationEnd?: (displayText: string, rawActionBlocks: string[]) => void;
  /** Fired once per executed action, in execution order. */
  onActionResult?: (result: ActionResult) => void;
}

/** Where the turn sends its telemetry events; swap in a fake in tests. */
export interface TelemetryPort {
  recordEvent: (event: TelemetryEvent) => void;
  generateTurnId: () => string;
  getSessionId: () => string;
  getAddonEnv?: () => string;
}

export interface ChatTurnDeps {
  getContextData: typeof getContextData;
  getContextDataWithDiagnostics?: typeof getContextDataWithDiagnostics;
  getExcelSettings: typeof getExcelSettings;
  getExcelSettingsWithDiagnostics?: typeof getExcelSettingsWithDiagnostics;
  sendMessage: typeof sendMessage;
  parseActions: typeof parseActions;
  executeActions: typeof executeActions;
  telemetry: TelemetryPort;
}

const defaultDeps: ChatTurnDeps = {
  getContextData,
  getContextDataWithDiagnostics,
  getExcelSettings,
  getExcelSettingsWithDiagnostics,
  sendMessage,
  parseActions,
  executeActions,
  telemetry: {
    recordEvent: recordTelemetryEvent,
    generateTurnId,
    getSessionId,
    getAddonEnv,
  },
};

/**
 * Why the loop stopped:
 * - "completed": the model emitted no further actions.
 * - "no-progress": an iteration's actions all failed to execute.
 * - "declined": an iteration's actions were all turned down at the
 *   confirmation prompt. Kept apart from "no-progress" because nothing went
 *   wrong here — the safety rail worked, and a cautious user should not end a
 *   turn in what reads as a fault (issue #47).
 * - "cap-reached": the iteration cap was hit while the model still had more to do.
 * - "stopped": the caller aborted the turn.
 * - "failed": the turn threw — a timeout, a network error, an API rejection.
 *   Reported to telemetry only; the error is rethrown, so no result carries it.
 */
export type StopReason =
  | "completed"
  | "no-progress"
  | "declined"
  | "cap-reached"
  | "stopped"
  | "failed";

const STOPPED_NOTICE =
  "Stopped at your request. Changes already applied are left in the workbook.";

export interface ChatTurnResult {
  /**
   * The conversation to pass back in as `history`. The loop's own follow-up
   * turns are left out: their action-result bookkeeping is spent once the
   * turn ends, and the assistant is recorded as what the user actually saw.
   */
  messages: TurnMessage[];
  fullResponse: string;
  displayText: string;
  actions: SpreadsheetAction[];
  actionResults: ActionResult[];
  iterations: number;
  stopReason: StopReason;
  /** Why the turn ended, when it didn't end of its own accord. */
  stopNotice?: string;
  /** Ties this turn to its telemetry trace; derive a support reference from it. */
  turnId: string;
}

/**
 * How the turn ended, how far it got, and where its time went. The loop keeps
 * this current as it runs, so a turn that throws can still be described. It
 * starts at "failed": an exception escaping before the loop has recorded an
 * outcome of its own is exactly what a failed turn is.
 *
 * The timings live here rather than in a parameter of their own for the same
 * reason the counts do — a failing turn's are the ones worth having, and only
 * a value the loop writes through as it runs survives an exception.
 */
interface TurnOutcome {
  iterations: number;
  stopReason: StopReason;
  /** Set only alongside `stopReason: "failed"`; see `TurnFinishedEvent`. */
  errorKind?: ErrorKind;
  errorMessage?: string;
  /** Charged as the loop reports each phase; read once at the envelope's end. */
  phases: PhaseClock;
  /** Unset until the turn's first streamed chunk arrives; see `TurnFinishedEvent`. */
  timeToFirstChunkMs?: number;
}

export async function runChatTurn(
  params: ChatTurnParams,
  callbacks: ChatTurnCallbacks = {},
  deps: Partial<ChatTurnDeps> = {},
): Promise<ChatTurnResult> {
  const resolved = {...defaultDeps, ...deps};
  if (deps.getContextData && !deps.getContextDataWithDiagnostics) {
    resolved.getContextDataWithDiagnostics = undefined;
  }
  if (deps.getExcelSettings && !deps.getExcelSettingsWithDiagnostics) {
    resolved.getExcelSettingsWithDiagnostics = undefined;
  }
  const {telemetry} = resolved;

  const turnId = telemetry.generateTurnId();
  const sessionId = telemetry.getSessionId();
  const addonEnv = telemetry.getAddonEnv?.() ?? getAddonEnv();
  const startedAt = Date.now();
  const maxIterations = clampIterationCap(params.maxIterations);
  telemetry.recordEvent({
    type: "turn_started",
    turnId,
    sessionId,
    agentId: params.agentId,
    addonEnv,
    maxIterations,
  });
  // A histogram sample of how deep this turn falls in its thread — separate
  // from turn_started so that event's shape (and every test asserting it)
  // stays as it was (issue #58).
  telemetry.recordEvent({
    type: "conversation_depth",
    turnId,
    sessionId,
    turnIndex: turnIndexOf(params.history),
  });

  // The loop runs behind this envelope so there is no exit path from which
  // `turn_finished` can be skipped — see ADR-0002 § "Every turn reports an
  // outcome" for why an unreported failure corrupts every percentile.
  const outcome: TurnOutcome = {
    iterations: 0,
    stopReason: "failed",
    phases: createPhaseClock(),
  };
  try {
    const result = await runTurnLoop(
      params,
      callbacks,
      resolved,
      {turnId, sessionId},
      outcome,
    );
    // Spread field by field rather than `...outcome`: the diagnostic fields
    // exist for telemetry, and a completed turn's result has no use for them.
    return {
      ...result,
      iterations: outcome.iterations,
      stopReason: outcome.stopReason,
      turnId,
    };
  } catch (err) {
    // An abort is the caller getting what they asked for, so it is not a
    // failure. Keyed on the error rather than on the signal because a real
    // API error can land in the moment the user hits Stop, and that one is
    // still a failure worth seeing.
    if (isAbortError(err) && params.signal?.aborted) {
      outcome.stopReason = "stopped";
    } else {
      // What broke, in the two forms telemetry needs it: a bounded kind for
      // the metric label, and the untouched message for the span.
      outcome.errorKind = classifyError(err);
      outcome.errorMessage = errorMessageOf(err);
    }
    throw err;
  } finally {
    // Closes whichever phase was open, so a turn that threw mid-wait still
    // reports that wait rather than discarding it.
    outcome.phases.stop();
    const finishedEvent: Extract<TelemetryEvent, {type: "turn_finished"}> = {
      type: "turn_finished",
      turnId,
      sessionId,
      agentId: params.agentId,
      addonEnv,
      maxIterations,
      stopReason: outcome.stopReason,
      iterations: outcome.iterations,
      durationMs: Date.now() - startedAt,
      phaseDurationsMs: outcome.phases.totals(),
    };
    if (outcome.timeToFirstChunkMs !== undefined) {
      finishedEvent.timeToFirstChunkMs = outcome.timeToFirstChunkMs;
    }
    if (outcome.errorKind !== undefined) {
      finishedEvent.errorKind = outcome.errorKind;
    }
    if (outcome.errorMessage !== undefined) {
      finishedEvent.errorMessage = outcome.errorMessage;
    }
    telemetry.recordEvent(finishedEvent);
  }
}

/**
 * The agentic loop itself: think, act, review, repeat until the model runs out
 * of actions or a rail stops it. Records its progress into `outcome` as it
 * goes — rather than only on return — so its caller can describe the turn
 * even when this throws.
 */
async function runTurnLoop(
  params: ChatTurnParams,
  callbacks: ChatTurnCallbacks,
  deps: ChatTurnDeps,
  ids: {turnId: string; sessionId: string},
  outcome: TurnOutcome,
): Promise<Omit<ChatTurnResult, "turnId" | "iterations" | "stopReason">> {
  const {
    getContextData: getCtx,
    getExcelSettings: getSettings,
    sendMessage: send,
    parseActions: parse,
    executeActions: execActions,
    telemetry,
  } = deps;
  const {turnId, sessionId} = ids;
  const contextDiagnostics = deps.getContextDataWithDiagnostics;

  const iterationCap = clampIterationCap(params.maxIterations);
  // The clock is driven off the same call the UI is, so the two cannot drift:
  // a phase the user was shown is a phase that was measured. Which also means
  // "running-actions" covers any confirmation prompt the user sat thinking
  // about — it is wall-clock time in a phase, not machine time.
  const reportPhase = (phase: TurnPhase, iteration: number) => {
    outcome.phases.enter(phase);
    callbacks.onPhase?.(phase, {iteration, of: iterationCap});
  };

  let contextData = "";
  let excelSettings: ExcelSettings | undefined;
  reportPhase("reading-workbook", 0);
  try {
    if (deps.getExcelSettingsWithDiagnostics) {
      const result = await deps.getExcelSettingsWithDiagnostics();
      excelSettings = result.settings;
      if (result.readFailed) {
        telemetry.recordEvent({
          type: "turn_degradation",
          turnId,
          sessionId,
          kind: "excel_settings_read_failure",
        });
      }
    } else {
      excelSettings = await getSettings();
    }
  } catch (err) {
    console.warn("Failed to read Excel settings:", err);
    logError("getExcelSettings", err);
    telemetry.recordEvent({
      type: "turn_degradation",
      turnId,
      sessionId,
      kind: "excel_settings_read_failure",
    });
  }
  const initialContext = await readContextData(
    getCtx,
    contextDiagnostics,
    telemetry,
    turnId,
    sessionId,
    // Only the turn's opening read reports workbook shape: it's what the
    // turn's duration, truncation and failure rate are correlated against,
    // and a sample per iteration would just repeat the same workbook (issue #58).
    true,
  );
  contextData = initialContext.data;

  let messages: TurnMessage[] = [...(params.history ?? [])];
  const allActions: SpreadsheetAction[] = [];
  const allActionResults: ActionResult[] = [];
  const responses: string[] = [];
  const displayTexts: string[] = [];

  const userTurn = buildUserTurn(params.userMessage, {
    settings: excelSettings,
    spreadsheetData: contextData,
  });
  let nextUserMessage = userTurn;
  let stopReason: StopReason = "completed";
  let validationRepairs = 0;

  for (let iteration = 1; iteration <= iterationCap; iteration++) {
    if (params.signal?.aborted) {
      stopReason = "stopped";
      break;
    }
    outcome.iterations = iteration;
    callbacks.onIterationStart?.();
    reportPhase(iteration === 1 ? "thinking" : "reviewing", iteration);

    const capped = capHistoryWithDiagnostics(
      pruneSnapshots([...messages, nextUserMessage]),
    );
    messages = capped.messages;
    if (capped.truncated) {
      telemetry.recordEvent({
        type: "turn_degradation",
        turnId,
        sessionId,
        kind: "history_truncated",
        originalSize: capped.originalCount,
        retainedSize: messages.length,
      });
    }

    const outgoing = toChatMessages(messages);
    callbacks.onPromptBuilt?.(outgoing);

    const requestSentAt = Date.now();
    const fullResponse = await send({
      agentId: params.agentId,
      messages: outgoing,
      apiKey: params.apiKey,
      stream: true,
      onChunk: (chunk) => {
        // Recorded once per turn, so this is the wait before the *user* saw
        // anything — the loop's own follow-ups are measured by `reviewing`.
        if (outcome.timeToFirstChunkMs === undefined) {
          outcome.timeToFirstChunkMs = Date.now() - requestSentAt;
        }
        callbacks.onChunk?.(chunk);
      },
      signal: params.signal,
    });

    // A stop lands mid-response: what streamed in stays on screen, but a
    // truncated reply is no use as history and its actions never run.
    if (params.signal?.aborted) {
      stopReason = "stopped";
      break;
    }

    responses.push(fullResponse);
    messages.push({role: "assistant", content: fullResponse});

    const {
      displayText,
      actions,
      invalidActionResults,
      invalidActionTelemetry = [],
      rawActionBlocks = [],
    } = parse(fullResponse);
    displayTexts.push(displayText);
    callbacks.onIterationEnd?.(displayText, rawActionBlocks);

    for (const invalid of invalidActionTelemetry) {
      telemetry.recordEvent({
        type: "invalid_action_output",
        turnId,
        sessionId,
        iteration,
        reason: invalid.reason,
        actionType: invalid.actionType,
      });
    }

    const iterationResults: ActionResult[] = [];
    const recordActionTelemetry = (
      result: ActionResult,
      actionType: string,
    ) => {
      const about = {turnId, sessionId, iteration, actionType};
      telemetry.recordEvent({
        ...about,
        type: "action_result",
        outcome: result.outcome,
        // A decline carries no error: the message only restates the question
        // the user answered, and filing it as one would tag the span red.
        errorMessage: result.outcome === "failure" ? result.message : undefined,
        errorCode: result.errorCode,
      });
      if (result.confirmation) {
        telemetry.recordEvent({
          ...about,
          type: "confirmation_result",
          source: result.confirmation.source,
          outcome: result.confirmation.outcome,
        });
      }
    };
    for (const result of invalidActionResults) {
      iterationResults.push(result);
      allActionResults.push(result);
      callbacks.onActionResult?.(result);
      recordActionTelemetry(result, "unknown");
    }
    if (actions.length > 0) {
      reportPhase("running-actions", iteration);
      const results = await asExcelFailure(() =>
        execActions(actions, params.confirm),
      );
      for (const [index, result] of results.entries()) {
        iterationResults.push(result);
        allActionResults.push(result);
        callbacks.onActionResult?.(result);
        recordActionTelemetry(result, actions[index]?.type ?? "unknown");
      }
      allActions.push(...actions);
    }

    if (params.signal?.aborted) {
      stopReason = "stopped";
      break;
    }

    const attempted = actions.length + invalidActionResults.length;
    if (attempted === 0) {
      stopReason = "completed";
      break;
    }

    const hasValidationFailure = iterationResults.some(isRepairableFailure);
    const onlyValidationFailures = iterationResults.every(isRepairableFailure);
    if (hasValidationFailure && validationRepairs >= MAX_VALIDATION_REPAIRS) {
      stopReason = "no-progress";
      break;
    }

    if (
      !iterationResults.some((r) => r.outcome === "success") &&
      !onlyValidationFailures
    ) {
      // Only a clean sweep of declines is a decline. Mixed with a real
      // failure, the failure is what stalled the turn and is the reason
      // worth reporting.
      stopReason = iterationResults.every((r) => r.outcome === "declined")
        ? "declined"
        : "no-progress";
      break;
    }

    if (iteration === iterationCap) {
      stopReason = "cap-reached";
      break;
    }

    if (hasValidationFailure) validationRepairs += 1;

    reportPhase("reading-workbook", iteration);
    const refreshedContext = await readContextData(
      getCtx,
      contextDiagnostics,
      telemetry,
      turnId,
      sessionId,
    );

    nextUserMessage = buildFollowUpTurn(iterationResults, {
      settings: excelSettings,
      spreadsheetData: refreshedContext.data,
    });
  }

  outcome.stopReason = stopReason;

  const stopNotice = buildStopNotice(
    stopReason,
    allActionResults,
    iterationCap,
  );

  let displayText = displayTexts.filter((t) => t.trim()).join("\n\n");
  if (stopNotice) {
    displayText = displayText ? `${displayText}\n\n${stopNotice}` : stopNotice;
  }

  return {
    messages: capHistory(
      pruneSnapshots([
        ...(params.history ?? []),
        userTurn,
        {role: "assistant", content: displayText || responses.join("\n\n")},
      ]),
    ),
    fullResponse: responses.join("\n\n"),
    displayText,
    actions: allActions,
    actionResults: allActionResults,
    stopNotice,
  };
}

/**
 * What to tell the user about an ending they didn't ask for, or `undefined`
 * when the turn ended of its own accord and the reply speaks for itself.
 *
 * A `switch` rather than a chain of `if`s so that adding a stop reason is a
 * type error here until it's been considered.
 */
function buildStopNotice(
  stopReason: StopReason,
  actionResults: ActionResult[],
  iterationCap: number,
): string | undefined {
  switch (stopReason) {
    case "cap-reached":
      return buildSurrenderMessage(actionResults, iterationCap);
    case "stopped":
      return STOPPED_NOTICE;
    case "completed":
      // The reply itself already says what happened.
      return undefined;
    case "no-progress":
      if (actionResults[actionResults.length - 1]?.retryable === true) {
        return "Stopped because the AI could not produce a valid action. Rejected actions were not executed.";
      }
      return actionResults[actionResults.length - 1]?.errorCode ===
        "script_validation"
        ? "Stopped because the generated script could not pass Excel API validation. Blocked scripts were not run."
        : undefined;
    case "declined":
      // The per-action bubble already reads "cancelled by user at the
      // confirmation prompt". A second notice would only add the support
      // error reference ChatPanel appends, and a decline is not an error.
      return undefined;
    case "failed":
      // Unreachable: a failed turn rethrows, so no result is ever built for
      // it and ChatPanel renders the error instead.
      return undefined;
    default: {
      const unhandled: never = stopReason;
      return unhandled;
    }
  }
}

function buildSurrenderMessage(
  actionResults: ActionResult[],
  iterationCap: number,
): string {
  const done: string[] = [];
  const notDone: string[] = [];
  for (const r of actionResults) {
    (r.outcome === "success" ? done : notDone).push(r.message);
  }

  let message = `Stopped after ${iterationCap} iterations.`;
  message += done.length
    ? ` Done: ${done.join("; ")}.`
    : " Done: nothing completed successfully.";
  if (notDone.length) {
    message += ` Not done: ${notDone.join("; ")}.`;
  }
  return message;
}

async function readContextData(
  getCtx: typeof getContextData,
  getCtxWithDiagnostics: typeof getContextDataWithDiagnostics | undefined,
  telemetry: TelemetryPort,
  turnId: string,
  sessionId: string,
  emitShape = false,
): Promise<{data: string}> {
  try {
    if (getCtxWithDiagnostics) {
      const result = await getCtxWithDiagnostics();
      if (result.degradation) {
        telemetry.recordEvent({
          type: "turn_degradation",
          turnId,
          sessionId,
          ...result.degradation,
        });
      }
      if (emitShape) {
        telemetry.recordEvent({
          type: "workbook_shape",
          turnId,
          sessionId,
          ...result.shape,
        });
      }
      return result;
    }
    return {data: await getCtx()};
  } catch (err) {
    console.warn("Failed to read spreadsheet data:", err);
    logError("getContextData", err);
    telemetry.recordEvent({
      type: "turn_degradation",
      turnId,
      sessionId,
      kind: "context_read_failure",
    });
    return {
      data:
        "[Workbook context unavailable: the workbook could not be read. " +
        "Do not assume the workbook is empty; tell the user to retry.]",
    };
  }
}

/**
 * Runs Office.js work, tagging anything that escapes it as an Excel failure.
 * `executeActions` handles a failing action itself, so a throw from here is
 * the workbook layer collapsing — and left unclassified it would arrive as
 * `unknown`, pointing the investigation at LibreChat for a problem on this
 * side of the wire.
 */
async function asExcelFailure<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    // A stop mid-action is still a stop, and an error that already knows what
    // it is knows better than this wrapper does.
    if (isAbortError(err) || err instanceof ClassifiedError) throw err;
    throw new ClassifiedError("excel", errorMessageOf(err));
  }
}
